/**
 * `AW2` · El modal «Cargar documento».
 *
 * `F1.md` §Formulario «Cargar documento» y §P0-1. Selección múltiple, tema por
 * lote, rechazo con mensaje, y **un resultado visible POR ARCHIVO**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS TRES DECISIONES QUE ESTE ARCHIVO LLEVA CON MARCADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **El rechazo del selector es una CORTESÍA, no la autoridad.** Lo que el
 *   navegador filtra sirve para no hacerle subir 40 MB a alguien por una
 *   conexión de oficina para enterarse después de que el formato no servía. Pero
 *   **lo que la pantalla muestra como RESULTADO es lo que contestó el borde, por
 *   archivo**: la lista de `IngestOutcome` y nada más. La pantalla no es el único
 *   camino de entrada —`arquitectura.html` §2 tiene a una persona de Elige
 *   cargando por API—, el `accept` de un `input[type=file]` se saltea
 *   arrastrando, y el **nombre duplicado no se puede saber desde el navegador**:
 *   lo sabe el UNIQUE de la base que puso `K2`. Por eso acá no se chequea contra
 *   el banco, ni una sola vez.
 *
 * ② **Las extensiones y el tamaño máximo llegan POR EL BORDE**, con
 *   `uploadConstraints()`. No hay ninguna constante de formato ni de tamaño en
 *   este archivo. Los dos son `[CONFIG]` por instancia y esto es **una sola build
 *   para todas las instancias**: una constante acá produce una pantalla que
 *   rechaza distinto que el banco, y el operador se entera cuando el documento
 *   aparece en `error` sin haber hecho nada mal.
 *
 * ③ **El resultado del lote se muestra POR ARCHIVO.** Un rechazo **no cierra el
 *   modal ni borra de la vista a los que sí entraron**. No hay ningún cartel
 *   único del lote: el borde devuelve `IngestOutcome[]` —uno por archivo, en
 *   orden— justamente para que el criterio negativo de §P0-1 sea visible, y «un
 *   criterio que el sistema cumple y la pantalla oculta se reporta como
 *   incumplido».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UN RECHAZO NO ES UNA EXCEPCIÓN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `contracts/admin-api` lo fija: formato, tamaño y nombre duplicado «son
 * resultados esperados de §P0-1 y viajan en su `IngestOutcome`». Este archivo
 * tiene dos caminos de error SEPARADOS y nunca se mezclan:
 *
 *   · `resultados` — la lista por archivo. Rechazos incluidos. NO es un error.
 *   · `errorDelLote` — que `upload()` haya lanzado: red caída, `admin-web`
 *     contestando 500, una respuesta que no se entiende. Ahí no hay resultados
 *     porque el lote no llegó.
 *
 * Tratar lo primero como lo segundo rompería el criterio negativo de §P0-1 desde
 * el último lugar donde se puede romper.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { ChangeEvent, DragEvent } from "react"
import { useBordeDeEscritura } from "../contexto-borde-escritura.tsx"
import type { IngestOutcome, UploadConstraints } from "../borde-escritura.ts"
import { Dialogo } from "./dialogo.tsx"

/**
 * La extensión de un nombre de archivo, **CON el punto y en minúscula**.
 *
 * ⚠️ Ésta es la línea que ya costó una corrida entera con modelo real. La forma
 * equivocada es `nombre.split(".").pop()`, que devuelve `"pdf"` —SIN punto—
 * contra una lista que los tiene (`[".doc", ".docx", ".pdf"]`): cero
 * coincidencias, todo archivo rechazado como «formato no soportado», y el propio
 * mensaje de error volviendo a agregar el punto para imprimirlo, de modo que en
 * pantalla se lee perfecto y la comparación nunca dio verdadero.
 *
 * `contracts/admin-api` fija la convención en el tipo mismo
 * (`readonly \`.${string}\`[]`) y avisa que «acá importa el doble: este valor
 * cruza a un navegador que lo va a usar para armar el `accept` de un
 * `input[type=file]`, y la convención se pierde justo en esa traducción».
 *
 * Se replica el comportamiento de `extname()` de Node, que es lo que usa el
 * doble del contrato para juzgar lo mismo del otro lado:
 *
 *   · `"informe.pdf"` → `".pdf"`
 *   · `"informe.final.PDF"` → `".pdf"` (última extensión, en minúscula)
 *   · `"informe"` → `""` — sin extensión, y `""` no es un `` `.${string}` ``,
 *     así que cae por formato en vez de colarse
 *   · `".gitignore"` → `""` — un punto inicial es un nombre oculto entero, no
 *     una extensión
 */
export function extensionDe(nombre: string): string {
  const punto = nombre.lastIndexOf(".")
  if (punto <= 0) return ""
  return nombre.slice(punto).toLowerCase()
}

/**
 * Tamaño en unidades legibles, sólo para el mensaje de rechazo.
 *
 * El límite en bytes es el del borde y no se toca: esto es cómo se IMPRIME. «El
 * archivo supera el tamaño máximo de 41943040 bytes» es un mensaje que obliga al
 * operador a contar ceros.
 */
export function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const unidades = ["kB", "MB", "GB"] as const
  let valor = bytes / 1024
  let i = 0
  while (valor >= 1024 && i < unidades.length - 1) {
    valor = valor / 1024
    i = i + 1
  }
  return `${valor.toFixed(valor < 10 ? 1 : 0)} ${unidades[i]}`
}

/** Un archivo que el selector no agregó, con por qué. */
export interface RechazoDelSelector {
  readonly nombre: string
  readonly motivo: string
}

/**
 * El reparto de una selección: qué se agrega al lote y qué rechaza el selector.
 *
 * **Función aparte y pura a propósito**: es la mitad de `guard` ① que se puede
 * ejercer sin montar nada, y es donde vive la comparación de extensiones.
 *
 * `restricciones === null` significa que el borde no las publicó. En ese caso
 * **no se rechaza nada**: la autoridad es el borde, y sin sus reglas la cortesía
 * no tiene con qué ser cortés. Inventar un default acá sería el `guard` ②
 * entrando por la puerta de atrás.
 *
 * Lo único que se mira contra lo ya elegido es el nombre repetido DENTRO de esta
 * misma selección —dos veces el mismo archivo del disco—, que es información que
 * el navegador sí tiene. **Contra el banco no se mira nada**: eso lo decide el
 * UNIQUE de la base y llega en el `IngestOutcome`.
 */
export function repartirSeleccion(
  entrantes: readonly File[],
  yaEnElLote: readonly File[],
  restricciones: UploadConstraints | null,
): { readonly aceptados: readonly File[]; readonly rechazos: readonly RechazoDelSelector[] } {
  const aceptados: File[] = []
  const rechazos: RechazoDelSelector[] = []
  const nombresTomados = new Set(yaEnElLote.map((archivo) => archivo.name))

  for (const archivo of entrantes) {
    if (nombresTomados.has(archivo.name)) {
      rechazos.push({ nombre: archivo.name, motivo: "Ya está en este lote." })
      continue
    }

    if (restricciones !== null) {
      const extension = extensionDe(archivo.name)
      if (!restricciones.acceptedExtensions.some((aceptada) => aceptada === extension)) {
        rechazos.push({
          nombre: archivo.name,
          motivo: `Formato no soportado. Se aceptan: ${restricciones.acceptedExtensions.join(", ")}.`,
        })
        continue
      }
      if (archivo.size > restricciones.maxBytes) {
        rechazos.push({
          nombre: archivo.name,
          motivo: `Supera el tamaño máximo de ${formatearTamano(restricciones.maxBytes)}.`,
        })
        continue
      }
    }

    nombresTomados.add(archivo.name)
    aceptados.push(archivo)
  }

  return { aceptados, rechazos }
}

/**
 * `guard` ② · las restricciones se PIDEN al borde, cada vez que se abre el modal.
 *
 * Cada vez y no una sola al arrancar: los dos valores son configuración de
 * instancia y **el mismo Management Tool es el que la edita**
 * (`arquitectura.html` §1). Cachearlas por la vida de la pestaña dejaría al
 * operador que acaba de subir el límite rechazando con el viejo.
 */
function useRestriccionesDeCarga(): {
  readonly restricciones: UploadConstraints | null
  readonly cargando: boolean
  readonly error: string | null
} {
  const borde = useBordeDeEscritura()
  const [restricciones, setRestricciones] = useState<UploadConstraints | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let vigente = true
    borde.uploadConstraints().then(
      (valor) => {
        if (!vigente) return
        setRestricciones(valor)
        setCargando(false)
      },
      (causa: unknown) => {
        if (!vigente) return
        setError(causa instanceof Error ? causa.message : String(causa))
        setCargando(false)
      },
    )
    return () => {
      vigente = false
    }
  }, [borde])

  return { restricciones, cargando, error }
}

export function ModalCarga({
  onCerrar,
  alTerminarUnLote,
}: {
  readonly onCerrar: () => void
  readonly alTerminarUnLote: () => void
}) {
  const borde = useBordeDeEscritura()
  const { restricciones, cargando: cargandoRestricciones, error: errorRestricciones } =
    useRestriccionesDeCarga()

  const [seleccionados, setSeleccionados] = useState<readonly File[]>([])
  const [rechazosDelSelector, setRechazosDelSelector] = useState<readonly RechazoDelSelector[]>([])
  const [tema, setTema] = useState("")
  const [enviando, setEnviando] = useState(false)
  const [resultados, setResultados] = useState<readonly IngestOutcome[] | null>(null)
  const [errorDelLote, setErrorDelLote] = useState<string | null>(null)

  const campoArchivos = useRef<HTMLInputElement>(null)

  /**
   * Agregar archivos, venga de donde venga.
   *
   * **El mismo camino para el botón y para el arrastre, y no es un detalle de
   * estilo:** el `accept` de un `input[type=file]` **no se aplica a los archivos
   * soltados** —el `guard` ① lo nombra: «se saltea arrastrando»—. Si el arrastre
   * tuviera su propio manejador, la mitad de la cortesía no existiría, y encima
   * por el camino que el PRD nombra primero («arrastrar o buscar»).
   */
  const agregar = useCallback(
    (entrantes: readonly File[]) => {
      if (entrantes.length === 0) return
      /**
       * El reparto se hace ACÁ y no adentro de un actualizador de estado.
       *
       * Un `setSeleccionados((previos) => …)` que además llamara a
       * `setRechazosDelSelector` desde adentro sería un actualizador con efecto,
       * y React los invoca DOS VECES en `StrictMode` —que es como corre esta
       * aplicación (`main.tsx`)—: cada archivo rechazado aparecería duplicado en
       * la lista de avisos, sólo en desarrollo y sólo a veces. Es un manejador de
       * un evento del operador, así que leer el estado del render actual es
       * correcto: no hay dos selecciones simultáneas.
       */
      const { aceptados, rechazos } = repartirSeleccion(entrantes, seleccionados, restricciones)
      setResultados(null)
      setErrorDelLote(null)
      if (aceptados.length > 0) setSeleccionados([...seleccionados, ...aceptados])
      if (rechazos.length > 0) setRechazosDelSelector([...rechazosDelSelector, ...rechazos])
    },
    [restricciones, seleccionados, rechazosDelSelector],
  )

  const alElegirArchivos = (evento: ChangeEvent<HTMLInputElement>) => {
    agregar(Array.from(evento.target.files ?? []))
    /**
     * El campo se vacía para que elegir DOS VECES el mismo archivo dispare el
     * evento de nuevo. Sin esto, un archivo rechazado por el selector no se puede
     * volver a elegir después de corregir el nombre sin recargar la pantalla.
     */
    if (campoArchivos.current !== null) campoArchivos.current.value = ""
  }

  const alSoltar = (evento: DragEvent<HTMLDivElement>) => {
    evento.preventDefault()
    agregar(Array.from(evento.dataTransfer.files))
  }

  const quitarDelLote = (indice: number) => {
    setSeleccionados((previos) => previos.filter((_, i) => i !== indice))
  }

  /**
   * Confirmar el lote.
   *
   * **Un solo llamado a `upload()` y nada después salvo mostrar lo que contestó.**
   * No se filtra, no se reordena, no se recorta: el emparejamiento archivo ⇄
   * resultado es POR POSICIÓN, que es lo que el contrato fija porque «dos
   * archivos del mismo lote pueden llamarse igual».
   */
  const confirmar = async () => {
    if (seleccionados.length === 0 || enviando) return
    setEnviando(true)
    setErrorDelLote(null)
    try {
      const salida = await borde.upload({
        files: seleccionados,
        /** `null` cuando no se escribió tema: «en KAI puede ir vacío». */
        topic: tema.trim() === "" ? null : tema.trim(),
      })
      setResultados(salida)
      /**
       * El lote se vacía porque ya no está pendiente: los que entraron y los que
       * no ahora viven en `resultados`, que es lo que se muestra. `guard` ③ · «un
       * rechazo no borra de la vista a los que sí entraron» — no se borran: pasan
       * de «pendientes» a «resultado», con su nombre y qué le pasó a cada uno.
       */
      setSeleccionados([])
      setRechazosDelSelector([])
      /**
       * La tabla de atrás se refresca. Es lo que hace visible «al confirmar, los
       * archivos aparecen en `subido`» sin esperar al `refetchInterval` de `AW1`.
       */
      alTerminarUnLote()
    } catch (causa) {
      /**
       * `guard` ③ · acá NO se cierra el modal ni se descarta la selección. El
       * lote no llegó, así que los archivos elegidos siguen elegidos y el
       * operador puede reintentar sin volver a buscarlos en el disco.
       */
      setErrorDelLote(causa instanceof Error ? causa.message : String(causa))
    } finally {
      setEnviando(false)
    }
  }

  const hayResultados = resultados !== null

  return (
    <Dialogo titulo="Cargar documento" onCerrar={onCerrar}>
      {/*
        `guard` ② · el `accept` sale de `acceptedExtensions`, TAL CUAL.

        Las extensiones vienen CON el punto, que es exactamente el formato que
        `accept` espera: se unen con coma y no se toca ni un carácter. Si el borde
        no las publicó, el atributo no va — un `accept` inventado sería la
        constante prohibida, escrita en el único lugar donde nadie la busca.
      */}
      <div
        className="carga__zona"
        onDragOver={(evento) => evento.preventDefault()}
        onDrop={alSoltar}
      >
        <label className="carga__etiqueta" htmlFor="carga-archivos">
          Arrastra los archivos aquí, o elígelos del disco
        </label>
        <input
          id="carga-archivos"
          ref={campoArchivos}
          type="file"
          multiple
          accept={restricciones?.acceptedExtensions.join(",")}
          onChange={alElegirArchivos}
          disabled={enviando}
        />
        <p className="carga__reglas">
          {cargandoRestricciones
            ? "Leyendo las reglas de carga…"
            : restricciones !== null
              ? `Formatos aceptados: ${restricciones.acceptedExtensions.join(", ")} · Tamaño máximo: ${formatearTamano(restricciones.maxBytes)}`
              : ""}
        </p>
      </div>

      {/*
        El borde no publicó las reglas.

        Se avisa y **se deja cargar igual**. Bloquear la carga acá convertiría la
        cortesía en la autoridad, que es lo contrario de `guard` ①: el que decide
        qué entra es el borde, y sigue pudiendo decidirlo. Lo que se pierde sin
        reglas es avisar antes de subir, y eso se dice con todas las letras en vez
        de suplirlo con constantes.
      */}
      {errorRestricciones !== null && (
        <p className="carga__aviso" role="status">
          No se pudieron leer las reglas de carga desde el servidor ({errorRestricciones}). Se puede
          cargar igual: el resultado de cada archivo lo decide el servidor.
        </p>
      )}

      <label className="carga__tema">
        <span>Tema / Categoría (opcional)</span>
        <input
          type="text"
          value={tema}
          onChange={(evento) => setTema(evento.target.value)}
          disabled={enviando}
          placeholder="Se aplica a todos los documentos de este lote"
        />
      </label>
      {/*
        `F1.md` §Formulario: «se aplica a todos los documentos del lote. Si se
        necesitan temas distintos, se cargan en lotes separados». Por eso hay UN
        campo para el lote y no uno por archivo: un campo por archivo permitiría
        expresar justo lo que el PRD manda partir en dos lotes, y `UploadCommand`
        tampoco lo podría transportar.
      */}
      <p className="carga__nota">
        Se aplica a todos los documentos de este lote. Para temas distintos, carga lotes separados.
      </p>

      {/* ── Lo que el SELECTOR no agregó · `guard` ① (la mitad de cortesía) ──── */}
      {rechazosDelSelector.length > 0 && (
        <section className="carga__seccion">
          <h3 className="carga__subtitulo">
            No se agregaron ({rechazosDelSelector.length})
          </h3>
          <ul className="carga__lista">
            {rechazosDelSelector.map((rechazo, indice) => (
              <li key={`${rechazo.nombre}-${indice}`} className="carga__item" data-aceptado="false">
                <span className="carga__nombre">{rechazo.nombre}</span>
                <span className="carga__motivo">{rechazo.motivo}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="boton"
            onClick={() => setRechazosDelSelector([])}
            disabled={enviando}
          >
            Descartar avisos
          </button>
        </section>
      )}

      {/* ── El lote pendiente ─────────────────────────────────────────────────── */}
      {seleccionados.length > 0 && (
        <section className="carga__seccion">
          <h3 className="carga__subtitulo">
            Se van a cargar ({seleccionados.length})
          </h3>
          <ul className="carga__lista">
            {seleccionados.map((archivo, indice) => (
              <li key={`${archivo.name}-${indice}`} className="carga__item">
                <span className="carga__nombre">{archivo.name}</span>
                <span className="carga__motivo">{formatearTamano(archivo.size)}</span>
                <button
                  type="button"
                  className="boton boton--chico"
                  onClick={() => quitarDelLote(indice)}
                  disabled={enviando}
                  aria-label={`Quitar ${archivo.name} del lote`}
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── `guard` ③ · EL RESULTADO, POR ARCHIVO ─────────────────────────────── */}
      {hayResultados && (
        <section className="carga__seccion" aria-live="polite">
          <h3 className="carga__subtitulo">Resultado de la carga</h3>
          <ul className="carga__lista carga__lista--resultado">
            {resultados.map((resultado, indice) => (
              /*
                La clave lleva el ÍNDICE y no sólo el nombre a propósito: dos
                archivos del mismo lote pueden llamarse igual —el contrato lo
                nombra— y el emparejamiento con su archivo es por POSICIÓN. Una
                clave por nombre colapsaría las dos filas justo en el caso que hay
                que mostrar bien.
              */
              <li
                key={`${resultado.name}-${indice}`}
                className="carga__item"
                data-aceptado={String(resultado.accepted)}
              >
                <span className="carga__nombre">{resultado.name}</span>
                <span className="carga__motivo">
                  {resultado.accepted
                    ? "Subido. Empezó a procesarse."
                    : /*
                        El motivo que trae el borde, TAL CUAL. Si vino vacío se
                        dice eso y no se inventa una causa: una razón inventada es
                        peor que ninguna, porque el operador la va a creer.
                      */
                      (resultado.reason ?? "Rechazado. El servidor no dio un motivo.")}
                </span>
              </li>
            ))}
          </ul>
          {/*
            NO hay un cartel de «N de M fallaron», y esa ausencia es el `guard` ③.
            El resumen de abajo cuenta, pero cada archivo tiene su renglón con su
            nombre y su motivo arriba: es un índice de la lista, no un reemplazo.
          */}
          <p className="carga__resumen">
            {resultados.filter((resultado) => resultado.accepted).length} de {resultados.length}{" "}
            entraron al banco. Los que no están detallados arriba, uno por uno.
          </p>
        </section>
      )}

      {/* ── El lote NO llegó. Esto no es un rechazo. ──────────────────────────── */}
      {errorDelLote !== null && (
        <p className="carga__error" role="alert">
          {/* `R1.1-F5-CA-85` · ~~«siguen acá»~~ → «siguen seleccionados» (`PRD_R1.1.md:904`). */}
          No se pudo enviar el lote: {errorDelLote}. Los archivos elegidos siguen seleccionados.
        </p>
      )}

      <footer className="dialogo__acciones">
        <button
          type="button"
          className="boton boton--primario"
          onClick={() => {
            void confirmar()
          }}
          disabled={seleccionados.length === 0 || enviando}
        >
          {enviando ? "Cargando…" : "Cargar"}
        </button>
        <button type="button" className="boton" onClick={onCerrar} disabled={enviando}>
          {/*
            «Cancelar» mientras hay algo por hacer, «Cerrar» cuando ya se cargó:
            después de un lote confirmado no hay nada que cancelar, y ofrecerlo
            sugiere que apretarlo deshace la carga. No hay deshacer.
          */}
          {hayResultados && seleccionados.length === 0 ? "Cerrar" : "Cancelar"}
        </button>
      </footer>
    </Dialogo>
  )
}
